import { z } from "zod";

const semverRegex =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

const semverFormatMessage = `Version format should match SemVer specification (major.minor.patch-prerelease+metadata).`;

export const semverSchema = z.string().regex(semverRegex, semverFormatMessage);

type SemverPrecedence = {
  release: [major: number, minor: number, patch: number];
  prerelease: string[];
};

// Build metadata is deliberately dropped: SemVer excludes it from precedence.
const parsePrecedence = (version: string): SemverPrecedence | undefined => {
  const match = semverRegex.exec(version);
  if (!match) return undefined;

  const [, major, minor, patch, prerelease] = match;

  return {
    release: [Number(major), Number(minor), Number(patch)],
    prerelease: prerelease ? prerelease.split(".") : [],
  };
};

const isNumericIdentifier = (identifier: string) => /^\d+$/.test(identifier);

const compareIdentifiers = (a: string, b: string) => {
  const aIsNumeric = isNumericIdentifier(a);
  const bIsNumeric = isNumericIdentifier(b);

  if (aIsNumeric && bIsNumeric) return Math.sign(Number(a) - Number(b));
  // Numeric identifiers always have lower precedence than alphanumeric ones.
  if (aIsNumeric !== bIsNumeric) return aIsNumeric ? -1 : 1;

  return a < b ? -1 : a > b ? 1 : 0;
};

const comparePrerelease = (a: string[], b: string[]) => {
  // A version without a prerelease outranks the same version with one.
  if (a.length === 0 || b.length === 0) return Math.sign(b.length - a.length);

  for (let index = 0; index < Math.max(a.length, b.length); index++) {
    const aIdentifier = a[index];
    const bIdentifier = b[index];

    if (aIdentifier === undefined) return -1;
    if (bIdentifier === undefined) return 1;

    const result = compareIdentifiers(aIdentifier, bIdentifier);
    if (result !== 0) return result;
  }

  return 0;
};

/**
 * True when `version` has lower SemVer precedence than `other`.
 * False for equal versions, and for anything that isn't valid SemVer.
 */
export const isSemverBefore = (version: string, other: string) => {
  const parsed = parsePrecedence(version);
  const parsedOther = parsePrecedence(other);
  if (!parsed || !parsedOther) return false;

  for (let index = 0; index < parsed.release.length; index++) {
    const difference = parsed.release[index] - parsedOther.release[index];
    if (difference !== 0) return difference < 0;
  }

  return comparePrerelease(parsed.prerelease, parsedOther.prerelease) < 0;
};
